import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { ChecklistEffects, DocumentTemplateDto, EmailTemplateDto, EmailTrigger, PlaceholderDef } from '@tww/shared';
import { CHECKLIST_TEMPLATE_ITEMS, EMAIL_RECIPIENT_ROLE_LABELS, PLACEHOLDERS, fmtDateTime, fmtNum, richToHtml, unknownPlaceholders } from '@tww/shared';
import { api } from '../../api/client';
import { useLookup } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, Note, Panel, Skeleton, StatusPill } from '../../ui/basics';
import { Checkbox, MultiSelect, NumberInput, Select, TextInput } from '../../ui/fields';
import { RichTextArea } from '../../ui/RichTextArea';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { useLeaveGuard, useUnsaved } from './adminUtil';

/** Was beim Erzeugen entsteht und wo es abgelegt wird (entspricht dem Erzeugen auf dem Server) */
const OUTPUT: Record<string, { file: string; place: string }> = {
  contract_owner: { file: 'Waldbesitzervertrag_<Kennung>.docx', place: 'Dateien der Aktion, verknüpft am Punkt „Projektvereinbarung Waldbesitzer“' },
  planting_confirmation: { file: 'Pflanzbestaetigung_<Kennung>.pdf', place: 'Dateien der Aktion' },
  site_visit: { file: 'Begehungsprotokoll_<Kennung>.pdf', place: 'Dateien der Aktion oder beim Begehungsprotokoll' },
  monitoring_doc: { file: 'Monitoring_<Fläche>_<Jahr>.pdf', place: 'Dateien der Aktion oder Ordner „Monitoring“ der Fläche' },
};

const DOC_DESC: Record<string, string> = {
  contract_owner: 'Word-Vorlage für den Vertrag mit Waldbesitzenden',
  planting_confirmation: 'Bestätigung der Pflanzung für Spendenpartner',
  site_visit: 'Begehungsprotokoll zur Unterschrift',
  monitoring_doc: 'Monitoring-Dokumentation je Fläche',
};

/** Verwaltung › Vorlagen: Dokumentvorlagen (Word-Datei, Version, Platzhalter, Test) und E-Mail-Vorlagen mit Vorschau */
export function TemplatesPage() {
  const [sp, setSp] = useSearchParams();
  const guard = useLeaveGuard();
  const tab = sp.get('tab') === 'email' ? 'email' : 'docs';
  return (
    <>
      <div className="seg mb-16" role="group" aria-label="Vorlagenart">
        <button type="button" className={tab === 'docs' ? 'on' : ''} onClick={() => tab !== 'docs' && guard(() => setSp({}, { replace: true }))}>
          Dokumentvorlagen
        </button>
        <button type="button" className={tab === 'email' ? 'on' : ''} onClick={() => tab !== 'email' && guard(() => setSp({ tab: 'email' }, { replace: true }))}>
          E-Mail-Vorlagen
        </button>
      </div>
      {tab === 'docs' ? <DocumentTemplates /> : <EmailTemplates />}
    </>
  );
}

/* ---------- Dokumentvorlagen ---------- */
function DocumentTemplates() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const write = can('templates', 'write');
  const q = useQuery({ queryKey: ['document-templates'], queryFn: () => api.get<{ items: DocumentTemplateDto[] }>('/templates/documents').then((r) => r.items) });
  const kinds = useLookup('document_kinds');
  const [sel, setSel] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; unknown: string[]; missing: string[]; error: string | null; action: { code: string } | null }>>({});
  const fileInput = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: async ({ key, file }: { key: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload<{ template: DocumentTemplateDto; unknown: string[]; error: string | null }>(`/templates/documents/${key}/upload`, fd);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['document-templates'] });
      if (r.error) toast(r.error, 'error');
      else toast(`Version ${r.template.version} hochgeladen${r.unknown.length ? ` · unbekannte Platzhalter: ${r.unknown.join(', ')}` : ''}`, r.unknown.length ? 'error' : 'ok');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const test = useMutation({
    mutationFn: (key: string) => api.post<{ ok: boolean; unknown: string[]; missing: string[]; error: string | null; action: { code: string } | null }>(`/templates/documents/${key}/test`),
    onSuccess: (r, key) => setTestResult((s) => ({ ...s, [key]: r })),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (q.isLoading) return <Skeleton />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const items = q.data!;
  const cur = items.find((t) => t.key === sel) ?? items[0];
  const kindLabel = (code: string) => kinds.list?.values?.find((k) => k.code === code)?.label ?? code;
  return (
    <div className="grid g12">
      <div className="panel">
        <div className="side-list side-list-pad">
          {items.map((t) => (
            <button key={t.key} type="button" className={cur?.key === t.key ? 'on' : ''} onClick={() => setSel(t.key)}>
              <span>
                {t.name}
                <span className="d">{t.engine === 'docx' ? (t.document ? `Word · Version ${t.version}` : 'Word · keine Datei') : 'PDF aus der Druckansicht'}</span>
              </span>
              {!t.active && <span className="n">inaktiv</span>}
            </button>
          ))}
        </div>
      </div>
      {cur && (
        <Panel title={cur.name} action={<StatusPill code={cur.active ? 'bestaetigt' : 'muted'} label={cur.active ? 'aktiv' : 'inaktiv'} />}>
          <div className="pb">
            <p className="mut small mt-0">{DOC_DESC[cur.key] ?? cur.description}</p>
            {cur.engine === 'docx' ? (
              <>
                <dl className="dl dl-narrow">
                  <dt>Aktuelle Datei</dt>
                  <dd>
                    {cur.document ? (
                      <>
                        <a className="link" href={`/api/v1/documents/${cur.document.id}/file?download=1`}>
                          {cur.document.original_name}
                        </a>{' '}
                        <span className="mut small">
                          Version {cur.version} · {fmtNum(Math.round((cur.document.size_bytes ?? 0) / 1024))} KB · {fmtDateTime(cur.document.created_at)}
                        </span>
                      </>
                    ) : (
                      <span className="miss">keine Datei hochgeladen</span>
                    )}
                  </dd>
                  <OutputInfo t={cur} kindLabel={kindLabel} />
                </dl>
                {write && (
                  <div className="btn-row my-12">
                    <button type="button" className="btn primary" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
                      <Icon name="upload" /> Neue Version hochladen (.docx)
                    </button>
                    <button type="button" className="btn" onClick={() => test.mutate(cur.key)} disabled={test.isPending}>
                      Vorlage testen
                    </button>
                    <input ref={fileInput} type="file" accept=".docx" hidden onChange={(e) => e.target.files?.[0] && (upload.mutate({ key: cur.key, file: e.target.files[0] }), (e.target.value = ''))} />
                  </div>
                )}
                {testResult[cur.key] && (
                  <Note kind={testResult[cur.key]!.ok && !testResult[cur.key]!.unknown.length ? 'good' : 'bad'}>
                    {testResult[cur.key]!.ok ? 'Vorlage lässt sich befüllen' : `Fehler: ${testResult[cur.key]!.error}`}
                    {testResult[cur.key]!.action ? ` (Beispiel ${testResult[cur.key]!.action!.code})` : ''}
                    {testResult[cur.key]!.unknown.length ? ` · unbekannte Platzhalter: ${testResult[cur.key]!.unknown.join(', ')}` : ''}
                    {testResult[cur.key]!.missing.length ? ` · leer bei der Beispielaktion: ${testResult[cur.key]!.missing.join(', ')}` : ''}
                  </Note>
                )}
                <h3 className="sub-h">Platzhalter in der Datei</h3>
                {cur.placeholders.length === 0 ? (
                  <div className="mut small">Keine Platzhalter erkannt. Platzhalter stehen in geschweiften Klammern in der Word-Datei, z. B. {'{area.municipality}'}.</div>
                ) : (
                  <PlaceholderTable list={cur.placeholders} />
                )}
                <details className="fs mt-14">
                  <summary>Alle verfügbaren Platzhalter</summary>
                  <PlaceholderTable list={PLACEHOLDERS} compact />
                </details>
              </>
            ) : (
              <>
                <dl className="dl dl-narrow">
                  <OutputInfo t={cur} kindLabel={kindLabel} />
                </dl>
                <Note kind="info">Dieses Dokument wird als PDF aus der Druckansicht erzeugt und braucht keine Word-Datei. Der Inhalt entspricht der Bildschirmansicht.</Note>
              </>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
/** Dokumentart, Dateiname und Ablageort des erzeugten Dokuments */
function OutputInfo({ t, kindLabel }: { t: DocumentTemplateDto; kindLabel: (code: string) => string }) {
  const out = OUTPUT[t.key];
  // Begehungsprotokoll und Monitoring-Doku haben feste Dokumentarten
  const kind = t.key === 'site_visit' ? 'protokoll' : t.key === 'monitoring_doc' ? 'monitoring_doku' : t.output_kind;
  return (
    <>
      <dt>Dokumentart</dt>
      <dd>{kindLabel(kind)}</dd>
      {out && (
        <>
          <dt>Dateiname</dt>
          <dd>{out.file}</dd>
          <dt>Ablage</dt>
          <dd>{out.place}</dd>
        </>
      )}
    </>
  );
}
function PlaceholderTable({ list, compact }: { list: PlaceholderDef[]; compact?: boolean }) {
  return (
    <div className="tw" style={compact ? { maxHeight: 280, overflow: 'auto' } : undefined}>
      <table>
        <tbody>
          {list.map((p) => (
            <tr key={p.key}>
              <td style={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{`{${p.key}}`}</td>
              <td className="small">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- E-Mail-Vorlagen ---------- */
type Draft = { name: string; subject: string; body: string; trigger_kind: 'after_item' | 'offset_days' | 'monitoring_inquiry'; after_item: string; offset_days: number; recipient_role: string; attachments: string[]; effects: ChecklistEffects; action_types: string[]; active: boolean };
const ATTACHMENT_OPTIONS = [
  { value: 'site_visit', label: 'Begehungsprotokoll (PDF, wird erzeugt)' },
  { value: 'contract_owner', label: 'Waldbesitzervertrag (Word, wird erzeugt)' },
  { value: 'planting_confirmation', label: 'Pflanzbestätigung (PDF, wird erzeugt)' },
  { value: 'monitoring_doc', label: 'Monitoring-Doku (PDF, wird erzeugt)' },
];
function toDraft(t: EmailTemplateDto): Draft {
  const tr = t.trigger as EmailTrigger;
  return { name: t.name, subject: t.subject, body: t.body, trigger_kind: 'after_item' in tr ? 'after_item' : 'offset_days' in tr ? 'offset_days' : 'monitoring_inquiry', after_item: 'after_item' in tr ? tr.after_item : 'besichtigung', offset_days: 'offset_days' in tr ? tr.offset_days : -28, recipient_role: t.recipient_role, attachments: t.attachments, effects: t.checklist_effects ?? {}, action_types: t.action_types ?? [], active: t.active };
}
function EmailTemplates() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const write = can('templates', 'write');
  const kinds = useLookup('document_kinds');
  const types = useLookup('action_types');
  const q = useQuery({ queryKey: ['email-templates'], queryFn: () => api.get<{ items: EmailTemplateDto[] }>('/templates/emails').then((r) => r.items) });
  const [sel, setSel] = useState<string | null>(null);
  const [d, setD] = useState<Draft | null>(null);
  const [preview, setPreview] = useState<{ subject: string; body: string; missing: string[]; action: { code: string } | null } | null>(null);
  const cur = q.data?.find((t) => t.key === sel) ?? q.data?.[0];
  useEffect(() => {
    if (cur) {
      setD(toDraft(cur));
      setPreview(null);
    }
  }, [cur?.key, cur?.updated_at]);
  const dirty = Boolean(cur && d && JSON.stringify(d) !== JSON.stringify(toDraft(cur)));
  const guard = useUnsaved(dirty);
  const save = useMutation({
    mutationFn: () => {
      const trigger: EmailTrigger = d!.trigger_kind === 'after_item' ? { after_item: d!.after_item } : d!.trigger_kind === 'offset_days' ? { offset_days: d!.offset_days } : { monitoring_inquiry: true };
      return api.patch<EmailTemplateDto>(`/templates/emails/${cur!.key}`, { name: d!.name, subject: d!.subject, body: d!.body, trigger, recipient_role: d!.recipient_role, attachments: d!.attachments, checklist_effects: Object.keys(d!.effects).length ? d!.effects : null, action_types: d!.action_types.length ? d!.action_types : null, active: d!.active, review_note: null });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-templates'] });
      toast('Vorlage gespeichert; wirkt auf neue Entwürfe');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const doPreview = useMutation({
    mutationFn: () => api.post<{ subject: string; body: string; missing: string[]; action: { code: string } | null }>(`/templates/emails/${cur!.key}/preview`, { subject: d!.subject, body: d!.body }),
    onSuccess: (r) => setPreview(r),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (q.isLoading) return <Skeleton />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const unknown = d ? unknownPlaceholders(d.subject + '\n' + d.body) : [];
  const itemOptions = CHECKLIST_TEMPLATE_ITEMS.map((i) => ({ value: i.key, label: i.title }));
  const attachmentOptions = [...ATTACHMENT_OPTIONS, ...kinds.values.filter((k) => !['vertrag', 'protokoll', 'pflanzbestaetigung', 'monitoring_doku'].includes(k.code)).map((k) => ({ value: k.code, label: `Dokumentart „${k.label}“ (falls vorhanden)` }))];
  return (
    <div className="grid g12">
      <div className="panel">
        <div className="side-list side-list-pad">
          {q.data!.map((t) => (
            <button key={t.key} type="button" className={cur?.key === t.key ? 'on' : ''} onClick={() => cur?.key !== t.key && guard(() => setSel(t.key))}>
              <span>
                {t.name}
                <span className="d">{t.trigger_label}</span>
              </span>
              {cur?.key === t.key && dirty ? <span className="n">geändert</span> : !t.active && <span className="n">inaktiv</span>}
            </button>
          ))}
        </div>
      </div>
      {cur && d && (
        <Panel
          title={cur.name}
          action={
            write && (
              <span className="btn-row">
                <button type="button" className="btn sm" onClick={() => doPreview.mutate()} disabled={doPreview.isPending}>
                  Vorschau mit Beispielaktion
                </button>
                <button type="button" className="btn sm primary" onClick={() => save.mutate()} disabled={save.isPending || !(dirty || cur.review_note)}>
                  Speichern
                </button>
              </span>
            )
          }
        >
          <div className="pb">
            {cur.review_note && <Note>Entwurf, noch nicht freigegeben. Speichern gibt die Vorlage frei.</Note>}
            {unknown.length > 0 && <Note kind="bad">Unbekannte Platzhalter: {unknown.join(', ')}</Note>}
            <div className="form mt-12">
              <TextInput label="Name" value={d.name} onChange={(v) => setD({ ...d, name: v })} disabled={!write} />
              <Select label="Empfänger" value={d.recipient_role} onChange={(v) => setD({ ...d, recipient_role: v })} options={Object.entries(EMAIL_RECIPIENT_ROLE_LABELS).map(([k, l]) => ({ value: k, label: l }))} placeholder="wählen" disabled={!write} />
              <Select label="Auslöser" value={d.trigger_kind} onChange={(v) => setD({ ...d, trigger_kind: v as Draft['trigger_kind'] })} options={[{ value: 'after_item', label: 'nach einem Checklistenpunkt' }, { value: 'offset_days', label: 'n Tage vor oder nach dem Aktionstag' }, { value: 'monitoring_inquiry', label: 'Monitoring-Nachfrage (1. Mai)' }]} placeholder="wählen" disabled={!write} />
              {d.trigger_kind === 'after_item' && <Select label="Punkt" value={d.after_item} onChange={(v) => setD({ ...d, after_item: v })} options={itemOptions} placeholder="wählen" disabled={!write} />}
              {d.trigger_kind === 'offset_days' && <NumberInput label="Tage relativ zum Aktionstag" value={d.offset_days} onChange={(v) => setD({ ...d, offset_days: v ?? 0 })} hint="negativ = vorher, z. B. -28; positiv = nachher, z. B. 7" disabled={!write} />}
              <TextInput label="Betreff" value={d.subject} onChange={(v) => setD({ ...d, subject: v })} full disabled={!write} />
              <RichTextArea label="Text" value={d.body} onChange={(v) => setD({ ...d, body: v })} rows={12} hint="Anrede („Sehr geehrter Herr …“) und Signatur der sendenden Person werden beim Entwurf automatisch ergänzt. Platzhalter in geschweiften Klammern, z. B. {action.date_de}." disabled={!write} />
              <MultiSelect label="Anhänge" value={d.attachments} onChange={(v) => setD({ ...d, attachments: v })} options={attachmentOptions} />
              <MultiSelect label="Nur für Aktionstypen (leer = alle)" value={d.action_types} onChange={(v) => setD({ ...d, action_types: v })} options={types.values.map((t) => ({ value: t.code, label: t.label }))} />
              <div className="field full">
                <label>Beim Senden in der Checkliste setzen</label>
                <EffectsEditor value={d.effects} onChange={(v) => setD({ ...d, effects: v })} disabled={!write} />
              </div>
              <Checkbox value={d.active} onChange={(v) => setD({ ...d, active: v })} label="Vorlage aktiv" disabled={!write} />
            </div>
            <details className="fs mt-14">
              <summary>Verfügbare Platzhalter</summary>
              <PlaceholderTable list={PLACEHOLDERS} compact />
            </details>
            {preview && (
              <div className="doc-preview mt-14">
                <div className="mut small">Vorschau{preview.action ? ` mit ${preview.action.code}` : ''}{preview.missing.length ? ` · leer: ${preview.missing.join(', ')}` : ''}</div>
                <p>
                  <strong>{preview.subject}</strong>
                </p>
                <div className="rich-text" dangerouslySetInnerHTML={{ __html: richToHtml(preview.body) }} />
              </div>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}

/** Punkte der Projektierung mit „Verschickt an“-Kästchen, die eine E-Mail anhaken kann */
const CHECK_EFFECT_ITEMS = ['infomaterial', 'karte_ablauf'];

/** Effekte als Auswahl: Kästchen in den Versandpunkten, Datumsfeld auf heute, Erinnerung in n Tagen */
function EffectsEditor({ value, onChange, disabled }: { value: ChecklistEffects; onChange: (v: ChecklistEffects) => void; disabled?: boolean }) {
  const setChecks = (key: string, field: string, opts: string[]) => {
    const n = { ...value };
    if (opts.length) n[key] = { ...(n[key] ?? {}), checks: { field, add: opts } };
    else delete n[key];
    onChange(n);
  };
  const vertragSent = value.vertrag?.set?.versendet === 'today';
  const pbReminder = value.pflanzbestaetigung?.reminder_days ?? null;
  return (
    <div className="form" style={{ marginTop: 6 }}>
      {CHECK_EFFECT_ITEMS.map((key) => {
        const item = CHECKLIST_TEMPLATE_ITEMS.find((i) => i.key === key);
        const field = item?.fields.find((f) => f.type === 'checks');
        if (!item || !field) return null;
        return <MultiSelect key={key} label={`„${item.title}“: anhaken`} value={value[key]?.checks?.add ?? []} onChange={(opts) => setChecks(key, field.key, opts)} options={(field.options ?? []).map((o) => ({ value: o, label: o }))} />;
      })}
      <Checkbox
        value={vertragSent}
        onChange={(v) => {
          const n = { ...value };
          if (v) n.vertrag = { set: { versendet: 'today' } };
          else delete n.vertrag;
          onChange(n);
        }}
        label="„Projektvereinbarung Waldbesitzer“: Versendet am = heute"
        disabled={disabled}
      />
      <NumberInput
        label="„Pflanzbestätigung“: Erinnerung in Tagen"
        value={pbReminder}
        onChange={(v) => {
          const n = { ...value };
          if (v != null) n.pflanzbestaetigung = { reminder_days: v };
          else delete n.pflanzbestaetigung;
          onChange(n);
        }}
        hint="leer = keine Erinnerung"
        disabled={disabled}
      />
    </div>
  );
}
